import { Link } from "react-router-dom";
import { projects } from "@/lib/portfolioData";
import { projectDetails } from "@/lib/projectDetails";

/* ---------- One CD / disk ---------- */
function Disk({ project, size = "md" }) {
  const sizeClass =
    size === "lg"
      ? "max-w-[420px] md:max-w-[520px]"
      : size === "sm"
        ? "max-w-[280px] md:max-w-[340px]"
        : "max-w-[340px] md:max-w-[420px]";

  const accentBg = project.accent === "lime" ? "bg-neon-lime" : "bg-neon-pink";
  const accentText =
    project.accent === "lime" ? "text-neon-lime" : "text-neon-pink";

  const hasDetail = !!projectDetails[project.slug];

  /* Disk visual is identical whether the project has a detail page or not.
     Clickable ones get a Link wrapper; static ones get a plain div. */
  const diskBody = (
    <>
      {/* The disk */}
      <div
        className={`absolute inset-0 rounded-full overflow-hidden shadow-[0_25px_60px_-15px_rgba(0,0,0,0.9)] ring-1 ring-black/10 transition-all duration-500 ${
          hasDetail ? "group-hover:ring-black/40" : ""
        }`}
      >
        {/* Spinning image */}
        <div
          className={`absolute inset-0 animate-spin-slow ${
            hasDetail ? "group-hover:[animation-duration:4s]" : ""
          }`}
        >
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
          {/* Concentric rings overlay */}
          <span
            aria-hidden="true"
            className="absolute inset-[6%] rounded-full border border-white/15"
          />
          <span
            aria-hidden="true"
            className="absolute inset-[18%] rounded-full border border-white/10"
          />
          <span
            aria-hidden="true"
            className="absolute inset-[34%] rounded-full border border-white/20"
          />
          {/* Light reflection */}
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "conic-gradient(from 0deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.18) 60deg, rgba(255,255,255,0) 120deg, rgba(255,255,255,0.12) 200deg, rgba(255,255,255,0) 280deg, rgba(255,255,255,0.08) 340deg, rgba(255,255,255,0) 360deg)",
              mixBlendMode: "overlay",
            }}
          />
        </div>

        {/* Static center hub */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-[22%] h-[22%] rounded-full bg-black flex items-center justify-center ring-1 ring-white/30">
            <div
              className={`w-[35%] h-[35%] rounded-full ${accentBg}`}
              aria-hidden="true"
            />
            <span
              aria-hidden="true"
              className="absolute inset-0 rounded-full border border-white/20"
            />
          </div>
        </div>

        {/* Hover label (only for clickable disks) */}
        {hasDetail && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.22em] ${accentText} bg-black/70 px-3 py-1.5 rounded-full mt-[60%]`}
            >
              View Project →
            </span>
          </div>
        )}
      </div>
    </>
  );

  return (
    <div
      data-testid={`project-card-${project.id}`}
      className={`group reveal w-full ${sizeClass} mx-auto`}
    >
      {hasDetail ? (
        <Link
          to={`/projects/${project.slug}`}
          data-testid={`project-open-${project.id}`}
          aria-label={`Open ${project.title}`}
          className="relative block w-full aspect-square focus:outline-none"
        >
          {diskBody}
        </Link>
      ) : (
        <div
          data-testid={`project-static-${project.id}`}
          className="relative block w-full aspect-square select-none"
          aria-label={`${project.title} — detail coming soon`}
        >
          {diskBody}
        </div>
      )}

      {/* Caption like a CD label */}
      <div className="mt-6 md:mt-8 flex items-start justify-between gap-4">
        <div>
          <p
            className={`font-mono text-[10px] uppercase tracking-[0.22em] ${accentText}`}
          >
            № {project.id} · Track {project.id}
            {!hasDetail && (
              <span className="ml-2 text-black/40">· soon</span>
            )}
          </p>
          <h3 className="mt-1 font-display text-2xl md:text-3xl leading-[1] tracking-tight">
            {project.title}
          </h3>
        </div>
        <div className="text-right font-mono text-[10px] uppercase tracking-[0.22em] text-black/50 whitespace-nowrap pt-1">
          <div>{project.year}</div>
          <div className="text-black/35">{project.role.split(" · ")[0]}</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Section ---------- */
export default function WorkGrid() {
  // Layout pattern for asymmetry on desktop
  const tiles = projects.map((p, i) => {
    const cfg = [
      { col: "lg:col-span-7", size: "lg", offset: "lg:mt-0" },
      { col: "lg:col-span-5", size: "sm", offset: "lg:mt-40" },
      { col: "lg:col-span-6", size: "md", offset: "lg:mt-0" },
      { col: "lg:col-span-6", size: "md", offset: "lg:mt-28" },
      { col: "lg:col-span-7", size: "lg", offset: "lg:mt-0" },
    ][i] || { col: "lg:col-span-6", size: "md", offset: "" };
    return { p, cfg };
  });

  return (
    <section
      id="work"
      data-testid="projects-grid"
      className="relative px-6 md:px-10 lg:px-16 py-28 md:py-36 border-t border-black/10 overflow-hidden"
    >
      {/* Blueprint dot grid background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
        }}
      />

      {/* Section header */}
      <div className="relative z-10 flex items-end justify-between gap-6 mb-16 md:mb-24">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-black/60">
            (02) — Selected Work · 2026 — 2023
          </p>
          <h2
            data-testid="work-heading"
            className="mt-4 font-display text-6xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight"
          >
            Spin the <span className="italic text-neon-pink">record.</span>
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.22em] text-black/40">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-neon-pink animate-blink" />
            Now playing
          </span>
          <span className="text-black/30">/ 5 tracks</span>
        </div>
      </div>

      {/* Disk grid */}
      <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-y-24 lg:gap-y-32 gap-x-10 lg:gap-x-16 items-start">
        {tiles.map(({ p, cfg }, i) => (
          <div key={p.id} className={`${cfg.col} ${cfg.offset}`} style={{ transitionDelay: `${i * 120}ms` }}>
            <Disk project={p} size={cfg.size} />
          </div>
        ))}
      </div>
    </section>
  );
}
